// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Tooltip, useSnackBar, useUniqueId } from '@usealloy/component-library';
import { BEST_EFFORT_PARSING_PLACEHOLDER_STRING } from 'flow/service/thresholds';
import {
  clampNumericValueToMinMax,
  convertDollarValueToRaw,
  formatRawValueToDollars,
  getMinMaxValueCap,
  getMinMaxValueCapTooltipContent,
  getRightAttributeValue,
} from './helpers';
import { computeRuleItemLengthLimit, isNumericDataType } from '../helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectRuleBuilderActiveSubEditor, selectRuleBuilderPanelBoxContainerWidth } from 'flow/store/redux/selectors';
import { truncateLabel } from 'flow/editor/components/thresholds/RuleThresholdDropdown/helpers';
import { StyledTextField } from './styles';
import { ThresholdContext } from '../context';
import { RuleBuilderContext } from '../../../context';
import { getDollarCurrencyRightAttributeValueMode } from 'flow/editor/components/thresholds/helpers';
import { getIsPreview } from '../../../helpers';

interface RHSTextFieldProps {
  onChange: (newValue: string) => void;
  shouldUseDefaultString: boolean;
  value: string;
}

const RHSTextField = ({
  onChange,
  shouldUseDefaultString,
  value,
}: RHSTextFieldProps) => {
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const addSnackBar = useSnackBar();

  const { keyboardFocus, editMode } = useContext(RuleBuilderContext);
  const { lhsDataType, populatedThreshold } = useContext(ThresholdContext);

  const dollarCurrencyRightAttributeValueMode = getDollarCurrencyRightAttributeValueMode(
    populatedThreshold.left);

  // convert raw values to display format (e.g., "123" → "$1.23" in dollar mode)
  const formatValueForDisplay = useCallback(
    (rawValue: string): string => (
      dollarCurrencyRightAttributeValueMode ? formatRawValueToDollars(rawValue) : rawValue
    ),
    [dollarCurrencyRightAttributeValueMode],
  );

  const [inputValue, setInputValue] = useState(formatValueForDisplay(value));
  const [isFocused, setIsFocused] = useState(false);

  const id = useUniqueId('textfield');
  const ref = useRef<HTMLInputElement>(null);

  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);

  useEffect(() => {
    keyboardFocus.register({ id, ref });
    return () => keyboardFocus.unregister(id);
  }, [id, keyboardFocus]);

  useEffect(() => {
    setInputValue(formatValueForDisplay(value));
  }, [value, dollarCurrencyRightAttributeValueMode, formatValueForDisplay]);

  const customPlaceholder = shouldUseDefaultString ? BEST_EFFORT_PARSING_PLACEHOLDER_STRING : '...';

  const minMaxValueCap = getMinMaxValueCap({
    lhsDataType,
    leftAction: populatedThreshold.left.action,
    leftAttribute: populatedThreshold.left.attribute,
  });

  const tooltipContent = minMaxValueCap
    ? getMinMaxValueCapTooltipContent({ lhsDataType, minMaxValueCap })
    : null;

  const convertInputToRawValue = (inputVal: string): string => (
    dollarCurrencyRightAttributeValueMode
      ? convertDollarValueToRaw(inputVal)
      : inputVal
  );

  const applyMinMaxClamping = (coercedValue: string): string => {
    const { clampedValue, wasChanged, changeType } = clampNumericValueToMinMax({
      value: Number.parseFloat(coercedValue),
      minMaxValueCap,
    });

    if (wasChanged) {
      addSnackBar({
        title: `The value was reset to the ${changeType} value allowed for this filter.`,
        type: 'warning',
      });
    }

    return clampedValue.toString();
  };

  const handleSubmitChange = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue === '') {
      // allow saving empty string only for string thresholds when best-effort parsing is OFF
      if (!shouldUseDefaultString) {
        setInputValue('');
        onChange('');
      }
      return;
    }

    // convert user input back to raw format for processing (e.g., "$1.23" → "123")
    const rawValue = convertInputToRawValue(trimmedValue);

    // apply data type validation and coercion - handles NaN → 0, rounds integers, applies default strings
    const coercedValue = getRightAttributeValue({
      lhsDataType,
      shouldUseDefaultString,
      value: rawValue,
    });

    // apply min/max clamping if needed
    const shouldClampValue = isNumericDataType(lhsDataType) && minMaxValueCap;
    const finalValue = shouldClampValue ? applyMinMaxClamping(coercedValue) : coercedValue;

    // convert final raw value back to display format for UI
    const displayValue = formatValueForDisplay(finalValue);

    // update UI with formatted value, send raw value to parent
    setInputValue(displayValue); // UI shows "$1.23"
    onChange(finalValue); // parent receives "123"
  };

  // final display value: show full text when focused, truncated when not focused
  const displayValue = isFocused ? inputValue : truncateLabel(inputValue, lengthLimit);

  const isPreview = getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId);

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={!tooltipContent}
    >
      <StyledTextField
        id={id}
        ref={ref}
        isPreview={isPreview}
        value={displayValue}
        onChange={e => setInputValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          handleSubmitChange();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSubmitChange();
          }
        }}
        placeholder={customPlaceholder}
        type="text"
        widthFitContent
      />
    </Tooltip>
  );
};

export default RHSTextField;

